import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  labToCssRgb,
  type AlternativeMode,
  type ApiClientErrorCode,
  type Paint,
} from '@pomoshnik/shared';
import { Header } from '@/components/Header/Header';
import { BottomBar } from '@/components/BottomBar/BottomBar';
import { AlternativesSection } from './AlternativesSection';

/**
 * Presenter Карточка краски (PRD §6 Block 3 Phase 4). См. ADR-0005.
 *
 * 4 состояния (P-021): loading / success / not-found / error.
 *
 * Hero: реальное `<img>` через `/paint-photos/${encodeURI(imageFilename)}`
 * (ADR-0011 dev storage). onError → swatch из dominantLab. Симметрично с
 * PaintResultCard fallback chain.
 *
 * 4 vertical action buttons:
 *   1. «Показать на мебели» — Pinterest external link.
 *   2. «Альтернативы» — accordion expand → AlternativesSection (4 mood
 *      кнопки: lighter/darker/warmer/cooler). Без disabled-state — paint 100%
 *      имеет dominantLab (open question §4).
 *   3. «Найти в магазинах» — Link на /paint/:globalId/shops (Phase 5 placeholder).
 *   4. «Переслать клиенту» — button → onShareClick (navigator.share + fallback).
 *
 * Локальный UI-state `altsExpanded` (accordion) живёт в Presenter, не в URL.
 * ADR-0005 разрешает `useState` для локального UI-state.
 */
export type PaintCardState = 'loading' | 'success' | 'not-found' | 'error';

export interface PaintCardViewProps {
  state: PaintCardState;
  paint?: Paint;
  error?: { code: ApiClientErrorCode; message: string };
  onAlternativeMode: (mode: AlternativeMode) => void;
  onShareClick: () => void;
  onBackToCatalog: () => void;
}

const PINTEREST_BASE = 'https://www.pinterest.com/search/pins/';
const BURGUNDY = '#8B1538';
const SWATCH_GREY = '#cccccc';

function buildPinterestUrl(paint: Paint): string {
  // Phase 4 Open Question §3: динамическая `category` (Кухня/Шкаф) вместо
  // hardcoded keyword — search-friendly для русскоязычного пользователя.
  // ralCode уже содержит «RAL» префикс (формат «RAL 7038») — НЕ добавляем
  // ещё один «RAL» в template (был bug «RAL RAL 7038», fix Phase 4 Шаг 9).
  const query = `${paint.name} ${paint.ralCode} ${paint.category.toLowerCase()} интерьер`;
  return `${PINTEREST_BASE}?q=${encodeURIComponent(query)}`;
}

export function PaintCardView(props: PaintCardViewProps) {
  return (
    <main className="min-h-screen flex flex-col bg-canvas">
      <Header title="Краска" />
      <section className="flex-1 p-6 overflow-y-auto">
        {props.state === 'loading' && <LoadingState />}
        {props.state === 'success' && props.paint && (
          <SuccessState
            paint={props.paint}
            onAlternativeMode={props.onAlternativeMode}
            onShareClick={props.onShareClick}
          />
        )}
        {props.state === 'not-found' && <NotFoundState onBackToCatalog={props.onBackToCatalog} />}
        {props.state === 'error' && (
          <ErrorState
            message={props.error?.message ?? ''}
            onBackToCatalog={props.onBackToCatalog}
          />
        )}
      </section>
      <BottomBar />
    </main>
  );
}

function SuccessState({
  paint,
  onAlternativeMode,
  onShareClick,
}: {
  paint: Paint;
  onAlternativeMode: (mode: AlternativeMode) => void;
  onShareClick: () => void;
}) {
  const [imageFailed, setImageFailed] = useState(false);
  const [altsExpanded, setAltsExpanded] = useState(false);
  const swatchBg = paint.dominantLab ? labToCssRgb(paint.dominantLab) : SWATCH_GREY;
  const showImage = !imageFailed;

  // Phase 5 placeholder: /paint/:globalId/shops появится с ShopsList wire-up
  // (ADR-0007 thin wrapper). Сейчас 404 — это OK для Phase 4.
  const shopsHref = `/paint/${encodeURIComponent(paint.globalId)}/shops`;

  return (
    <div className="flex flex-col gap-5">
      {/* Hero: real <img>, onError → swatch fallback */}
      <div
        className="w-full aspect-video rounded-xl border-[1.5px] border-icon-border overflow-hidden"
        style={!showImage ? { background: swatchBg } : undefined}
        aria-label={!showImage ? 'Образец цвета краски' : undefined}
        role={!showImage ? 'img' : undefined}
      >
        {showImage && (
          <img
            src={`/paint-photos/${encodeURI(paint.imageFilename)}`}
            alt={`Краска ${paint.name}, ${paint.ralCode}`}
            loading="eager"
            decoding="async"
            onError={() => setImageFailed(true)}
            className="w-full h-full object-cover"
          />
        )}
      </div>

      {/* Name + RAL badge + category badge + brand */}
      <div className="flex flex-col gap-2">
        <h2 className="font-display text-h2 text-ink m-0">{paint.name}</h2>
        <div className="flex flex-wrap gap-2">
          <span
            aria-label={`RAL-код: ${paint.ralCode}`}
            className="self-start inline-block rounded-full text-white text-sm px-3 py-1 font-medium"
            style={{ backgroundColor: BURGUNDY }}
          >
            {paint.ralCode}
          </span>
          <span
            aria-label={`Категория: ${paint.category}`}
            className="self-start inline-block rounded-full text-sm px-3 py-1 bg-icon-bg text-separator"
          >
            {paint.category}
          </span>
        </div>
        <p className="text-muted text-base m-0">
          Бренд: <span className="text-ink font-semibold">{paint.brand}</span>
        </p>
      </div>

      {/* 4 vertical action buttons (P-019). */}
      <div className="flex flex-col gap-3">
        <a
          href={buildPinterestUrl(paint)}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Показать на мебели: ${paint.name}`}
          className={[
            'w-full min-h-tap rounded-lg bg-card border-[1.5px] border-accent text-ink',
            'text-[16px] py-3 px-4 flex items-center gap-3',
            'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
          ].join(' ')}
        >
          <span aria-hidden="true">▶</span>
          <span className="flex-1 text-left">Показать на мебели</span>
          <span aria-hidden="true" className="text-muted">
            ↗
          </span>
        </a>

        <button
          type="button"
          aria-expanded={altsExpanded}
          onClick={() => setAltsExpanded((v) => !v)}
          className={[
            'w-full min-h-tap rounded-lg bg-card border-[1.5px] text-ink',
            'text-[16px] py-3 px-4 flex items-center gap-3',
            altsExpanded ? 'border-cta' : 'border-accent',
            'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
          ].join(' ')}
        >
          <span aria-hidden="true">⚖</span>
          <span className="flex-1 text-left">Альтернативы</span>
          <span aria-hidden="true" className="text-muted">
            {altsExpanded ? '⌄' : '›'}
          </span>
        </button>
        {altsExpanded && (
          <div className="pl-3">
            <AlternativesSection onSelect={onAlternativeMode} />
          </div>
        )}

        <Link
          to={shopsHref}
          aria-label={`Найти в магазинах: ${paint.name}`}
          className={[
            'w-full min-h-tap rounded-lg bg-card border-[1.5px] border-accent text-ink',
            'text-[16px] py-3 px-4 flex items-center gap-3',
            'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
          ].join(' ')}
        >
          <span aria-hidden="true">🛒</span>
          <span className="flex-1 text-left">Найти в магазинах</span>
          <span aria-hidden="true" className="text-muted">
            ›
          </span>
        </Link>

        <button
          type="button"
          onClick={onShareClick}
          aria-label="Переслать клиенту"
          className={[
            'w-full min-h-tap rounded-lg bg-card border-[1.5px] border-accent text-ink',
            'text-[16px] py-3 px-4 flex items-center gap-3',
            'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
          ].join(' ')}
        >
          <span aria-hidden="true">↗</span>
          <span className="flex-1 text-left">Переслать клиенту</span>
        </button>
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[40vh] gap-3">
      <p className="text-muted text-[18px] m-0">Загружаем…</p>
    </div>
  );
}

function NotFoundState({ onBackToCatalog }: { onBackToCatalog: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4 text-center">
      <p className="text-muted text-[18px] max-w-[280px] m-0 leading-normal">Краска не найдена</p>
      <button
        type="button"
        onClick={onBackToCatalog}
        className="min-h-tap rounded-lg bg-cta border-b-2 border-cta-shadow text-ink font-semibold text-base px-6 py-3"
      >
        Назад в каталог
      </button>
    </div>
  );
}

function ErrorState({
  message,
  onBackToCatalog,
}: {
  message: string;
  onBackToCatalog: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4 text-center">
      <p className="text-error text-[18px] max-w-[280px] m-0 leading-normal">
        Не удалось получить ответ. Проверьте интернет и попробуйте снова.
      </p>
      {message && <p className="text-muted text-sm">{message}</p>}
      <button
        type="button"
        onClick={onBackToCatalog}
        className="min-h-tap rounded-lg bg-cta border-b-2 border-cta-shadow text-ink font-semibold text-base px-6 py-3"
      >
        Назад в каталог
      </button>
    </div>
  );
}
